// src/utils/api.js
import axios from 'axios';
import { getUserCache, setUserCache } from './userCache';

// IMPORTANT: All network calls MUST be relative through /api to avoid CORS in the browser.



// Always go through the same-origin proxy in dev and prod
export const API_BASE = '/api';

// ---- Global auth-aware fetch wrapper (handles missing Authorization & 401 retry once) ----
const __origFetch = (typeof window !== 'undefined' && window.fetch) ? window.fetch.bind(window) : fetch;
async function __authFetch(input, init = {}) {
  // Don’t wrap refresh endpoint to avoid recursion (if you add /auth/refresh later)
  const url = typeof input === 'string' ? input : (input?.toString?.() || '');
  const isRefresh = url.includes('/auth/refresh');

  // Inject Authorization from localStorage if caller didn’t provide
  const headers = new Headers(init?.headers || {});
  if (!headers.has('Authorization') && typeof window !== 'undefined') {
    const token = localStorage.getItem('clairifi_token') || localStorage.getItem('token');
    if (token) headers.set('Authorization', `Bearer ${token}`);
  }

  const first = await __origFetch(input, { ...init, headers });
  if (first.status !== 401 || isRefresh) return first;

  // Optional one-shot retry hook (kept inert unless you add /auth/refresh)
  try {
    const auth = headers.get('Authorization');
    if (!auth) return first;
    const oldToken = auth.replace(/^Bearer\s+/i, '');
    // If you later add a refresh route, uncomment next lines:
    // const r = await __origFetch(`${API_BASE}/auth/refresh`, { method: 'POST', headers: { Authorization: `Bearer ${oldToken}` } });
    // if (r.ok) {
    //   const { token: newToken } = await r.json();
    //   if (newToken) {
    //     localStorage.setItem('clairifi_token', newToken);
    //     const retryHeaders = new Headers(init?.headers || {});
    //     retryHeaders.set('Authorization', `Bearer ${newToken}`);
    //     return await __origFetch(input, { ...init, headers: retryHeaders });
    //   }
    // }
  } catch { /* fall through */ }

  return first;
}
// Patch global fetch so all calls benefit automatically
if (typeof window !== 'undefined' && window.fetch) {
  window.fetch = __authFetch;
}

// ---- Chat draft helpers (frontend-only fallback so build doesn't break) ----
export async function getChatDraft() {
  try {
    const raw = localStorage.getItem('clairifi_chat_draft');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export async function saveChatDraft(draft) {
  try {
    localStorage.setItem('clairifi_chat_draft', JSON.stringify(draft || {}));
    return { ok: true };
  } catch (e) {
    return { ok: false, error: String(e) };
  }
}

/** Axios client (used by some calls) */
export const createApiClient = () => axios.create({ baseURL: API_BASE });
const api = createApiClient();

/** Always include Authorization if we can find a token. */
export function authHeader(explicitToken) {
  // Prefer the token AppContext writes; fall back to older key for compatibility
  const fromStorage =
    (typeof localStorage !== 'undefined' &&
      (localStorage.getItem('token') || localStorage.getItem('clairifi_token'))) ||
    null;

  const t = explicitToken || fromStorage;
  return t ? { Authorization: `Bearer ${t}` } : {};
}

/* ----------------------------
   Lightweight client-side cache
   ---------------------------- */
const _memCache = new Map();

function _stableStringify(obj) {
  const allKeys = [];
  JSON.stringify(obj, function (key, value) { allKeys.push(key); return value; });
  allKeys.sort();
  return JSON.stringify(obj, allKeys);
}

function _hash32(str) {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0).toString(16);
}

export function computeFingerprint(obj) {
  return _hash32(_stableStringify(obj));
}

export function getCached(ns, key) {
  const memKey = `${ns}:${key}`;
  if (_memCache.has(memKey)) return _memCache.get(memKey);
  try {
    const raw = localStorage.getItem(memKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    _memCache.set(memKey, parsed);
    return parsed;
  } catch {
    return null;
  }
}

export function setCached(ns, key, value) {
  const memKey = `${ns}:${key}`;
  _memCache.set(memKey, value);
  try {
    localStorage.setItem(memKey, JSON.stringify(value));
  } catch {}
}

/* ----------------------------
   Small helpers
   ---------------------------- */
async function _handleFetch(res) {
  const ct = res.headers.get('content-type') || '';
  const clen = res.headers.get('content-length');
  console.debug('[api] fetch', res.url, res.status, ct, 'len=', clen || '(chunked)');

  const body = ct.includes('application/json') ? await res.json() : await res.text();
  if (!res.ok) {
    let nice = `HTTP ${res.status}`;
    if (res.status === 403 && body && typeof body === 'object' && (body.detail || body.message)) {
      nice = body.detail || body.message;
    } else if (res.status === 404) {
      // Common case for missing comparison on an upload
      const detail = (body && typeof body === 'object' && (body.detail || body.message))
        ? (body.detail || body.message)
        : 'No comparison stored for this upload';
      nice = detail;
    }
    const err = new Error(nice);
    err.status = res.status;
    err.body = body;
    throw err;
  }

  return body;
}

/* ----------------------------
   Health / Auth / Account
   ---------------------------- */
export async function health() {
  const res = await fetch(`${API_BASE}/health`);
  return _handleFetch(res);
}

export async function getUserProfile(token) {
  // 1) Read identity/basic profile
  const meRes = await fetch(`${API_BASE}/auth/me`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const me = await _handleFetch(meRes);

  // 2) Always reconcile plan/limits from backend authority (Stripe-aware)
  const limitsRes = await fetch(`${API_BASE}/account/limits`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const limits = await _handleFetch(limitsRes);

  // 3) Return a merged snapshot; prefer /account/limits for plan/trial/limits
  return {
    ...me,
    plan: limits?.plan ?? me?.plan ?? null,
    trial_active: limits?.trial_active ?? null,
    trial_expires_at: limits?.trial_expires_at ?? null,
    uploads_used: limits?.uploads_used ?? null,
    uploads_limit: limits?.uploads_limit ?? null,
    uploads_remaining: limits?.uploads_remaining ?? null,
  };
}


export async function refreshToken(oldToken) {
  const res = await fetch(`${API_BASE}/auth/refresh`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${oldToken}` },
  });
  const data = await _handleFetch(res);
  return data?.token;
}

export async function fetchAccountUsage(token) {
  const res = await fetch(`${API_BASE}/account/usage`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return _handleFetch(res);
}
/** Authoritative membership snapshot (reconciles on backend) */
export async function fetchAccountLimits(token) {
  const res = await fetch(`${API_BASE}/account/limits`, {
    headers: (token ? { Authorization: `Bearer ${token}` } : {}),
  });
  return _handleFetch(res);
}

/** Force a Stripe ↔ DB resync on demand, return same shape as /account/limits */
export async function syncSubscription(token) {
  const res = await fetch(`${API_BASE}/account/subscription/sync`, {
    method: 'POST',
    headers: (token ? { Authorization: `Bearer ${token}` } : {}),
  });
  return _handleFetch(res);
}

/* ----------------------------
   Uploads
   ---------------------------- */
export async function uploadFile(file, token) {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`${API_BASE}/upload/`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: form,
  });
  return _handleFetch(res);
}

export async function fetchUploadHistory(token) {
  const res = await fetch(`${API_BASE}/account/uploads`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return _handleFetch(res);
}
export async function clearUploadHistory(token) {
  const res = await fetch(`${API_BASE}/account/uploads/clear`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  return _handleFetch(res);
}

export async function restoreUploadHistory(token) {
  const res = await fetch(`${API_BASE}/account/uploads/show-all`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  return _handleFetch(res);
}

/** NEW: fetch the stored comparison summary for a given uploaded filename (with raw debug capture) */
export async function getUploadComparison(filename, token) {
  if (!filename) throw new Error("filename is required");

  const url = `${API_BASE}/account/uploads/${encodeURIComponent(filename)}/comparison`;
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const res = await fetch(url, { headers });

  // Capture raw response for debug (without consuming the original stream)
  const rawText = await res.clone().text();
  let parsed = null;
  let errorMeta = null;

  try {
    parsed = await _handleFetch(res);
    return parsed;
  } catch (err) {
    errorMeta = { status: err.status, message: err.message, body: err.body };
    throw err;
  } finally {
    try {
      const h = {};
      res.headers.forEach((v, k) => { h[k] = v; });
      window.__lastComparisonFetch = {
        url,
        status: res.status,
        ok: res.ok,
        headers: h,
        raw: rawText,
        parsed,
        error: errorMeta,
        ts: new Date().toISOString()
      };
      console.debug('[api][comparison] __lastComparisonFetch', window.__lastComparisonFetch);
    } catch (_) {
      // ignore debug failures
    }
  }
}


// Reload a previously uploaded CSV into active session/app_state
export async function reloadPriorUpload(filename, token) {
  if (!filename) throw new Error("filename is required");
  const base = (typeof process !== "undefined" && process.env && process.env.REACT_APP_API_BASE)
    ? process.env.REACT_APP_API_BASE
    : "/api";

  const res = await fetch(`${base}/account/uploads/${encodeURIComponent(filename)}`, {
    method: "GET", // your backend loads the file on GET
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    credentials: "include",
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`reloadPriorUpload failed: ${res.status} ${text}`);
  }

  // Endpoint usually returns JSON or nothing; we don't strictly need the body.
  try { return await res.json(); } catch { return null; }
}


export async function loadPastUpload(filename, token) {
  console.debug('[api] GET /account/uploads/%s', filename);

  const res = await fetch(`${API_BASE}/account/uploads/${encodeURIComponent(filename)}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return _handleFetch(res);
}

/** poll last upload status (added) */
export async function fetchLastUpload(token) {
  const res = await fetch(`${API_BASE}/account/last`, {
    headers: (token ? { Authorization: `Bearer ${token}` } : {}),
  });
  return _handleFetch(res);
}
/* ----------------------------
   Feedback
   ---------------------------- */
export async function sendFeedback({ email, business_name = null, message, token } = {}) {
  if (!email || !message) {
    const e = new Error('Email and message are required');
    e.status = 400;
    throw e;
  }
  const res = await fetch(`${API_BASE}/feedback`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ email, business_name, message }),
  });
  return _handleFetch(res);
}

/* ----------------------------
   Cache wipe helpers
   ---------------------------- */
export function wipeFrontendCaches() {
  try {
    if (typeof window !== 'undefined' && window.__apiMem?.clear) window.__apiMem.clear();
  } catch {}
  try { _memCache.clear?.(); } catch {}
  try {
    const PRESERVE_EXACT = new Set([
      'clairifi:hasUploaded',
      'clairifi:companyName',
    ]);
    const toDelete = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k) continue;

      // Namespaced frontend/memory caches
      if (k.startsWith('frontend:') || k.startsWith('modelInsights') || k === 'forecast:latest') {
        toDelete.push(k);
        continue;
      }

      // User-scoped caches are stored as: clairifi:<userId>:<namespace>
      // Global flags like 'clairifi:hasUploaded' do NOT have a second colon — preserve them.
      if (k.startsWith('clairifi:')) {
        if (PRESERVE_EXACT.has(k)) continue;                // keep global flags
        const rest = k.slice('clairifi:'.length);
        const hasUserNamespace = rest.includes(':');
        if (hasUserNamespace) toDelete.push(k);             // clear user-scoped caches only
        // else: global, non-cache key (preserve)
      }
    }
    toDelete.forEach((k) => localStorage.removeItem(k));
  } catch {}
}

export async function wipeAllCaches(token) {
  wipeFrontendCaches();
  try {
    await fetch(`${API_BASE}/account/wipe-cache`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
  } catch {}
}

/* ----------------------------
   Forecast / Strategy / CFO / Model
   ---------------------------- */
export function getCachedForecast(userId) {
  if (!userId) return null;
  try {
    let obj = getUserCache(userId, 'forecast:last');
    let payload = obj?.payload || null;

    if (!payload) {
      // Backward compatibility: older key and/or stored the raw payload directly
      const legacy = getUserCache(userId, 'forecast:latest');
      payload = legacy?.payload || legacy || null;
    }

    if (payload && Array.isArray(payload.points) && payload.points.length > 0) return payload;
  } catch {}
  return null;
}


export function clearCachedForecast(userId) {
  try { setUserCache(userId, 'forecast:last', null); } catch {}
}

export const getForecastFromCache = getCachedForecast;
export const clearForecastCache  = clearCachedForecast;
// Try to infer minimal column types from a tabular dataset (demo fallback)
export function inferColumnTypesFromRows(rows = []) {
  if (!Array.isArray(rows) || rows.length === 0) return {};
  const sample = rows[0];
  const keys = Object.keys(sample).map(k => [k, String(k).toLowerCase().trim()]);

  const findKey = (needles) =>
    (keys.find(([orig, low]) => needles.some(n => low.includes(n))) || [null])[0];

  // ---- period fields (broadened)
  const year    = findKey(['year']);
  const quarter = findKey(['quarter', 'qtr', 'q ']); // allow q1/q2… style labels
  const month   = findKey(['month', 'mo']);
  let date      = findKey(['date', 'dt']);

  // Fallback: common single-column time labels like "period", "time", "timeline", "label"
  if (!date) {
    const periodLike = findKey(['period', 'time', 'timeline', 'label']);
    if (periodLike) {
      // If the first row’s value looks like a date-ish or Q* string, accept it as "date"
      const v = sample[periodLike];
      if (typeof v === 'string' && v.trim()) date = periodLike;
    }
  }

  const period = {};
  if (year)    period.year    = year;
  if (quarter) period.quarter = quarter;
  if (month)   period.month   = month;
  if (date)    period.date    = date;

  // ---- core financials (keep your demo-friendly matching)
  const lc = (s) => String(s || '').toLowerCase();
  const findMoney = (needles) =>
    (keys.find(([orig, low]) => needles.some(n => low.includes(n))) || [null])[0];

  const revenue     = findMoney(['total revenue', 'revenue', 'sales']);
  const expenses    = findMoney(['total expenses', 'expenses', 'opex', 'operating expenses']);
  const grossProfit = findMoney(['gross profit', 'gross margin']);
  const netIncome   = findMoney(['net income', 'profit', 'net profit']);

  const ct = {};
  if (Object.keys(period).length) ct.period = period;
  if (revenue)     ct.revenue      = revenue;
  if (expenses)    ct.expenses     = expenses;
  if (grossProfit) ct.gross_profit = grossProfit;
  if (netIncome)   ct.net_income   = netIncome;

  return ct;
}
export async function fetchForecast(arg = {}) {
  const {
    // new shape (from ForecastPage)
    rows,
    columnTypes,
    companyName,
    userId,

    // legacy shape / options
    horizon = 6,
    evaluation_mode,
    force = false,
  } = arg;

  const fp = Array.isArray(rows) && rows.length
    ? computeFingerprint({ rows, columnTypes: columnTypes || {}, companyName: companyName || '' })
    : null;
  const userCacheKey = fp ? `forecast:${fp}` : null;


  // Only trust cache when we have a dataset fingerprint
  if (!force && userId && fp) {
    try {
      const cached = getUserCache(userId, `forecast:${fp}`);
      const payload = cached?.payload;
      if (payload && Array.isArray(payload.points) && payload.points.length) return payload;
    } catch {}
  }


  // Resolve column types:
  // - if caller provided a non-empty mapping, use it
  // - else, if we are sending rows, infer from those rows
  // - else, leave undefined so we do NOT send an empty object
  let mappedColumnTypes;
  if (columnTypes && Object.keys(columnTypes).length > 0) {
    mappedColumnTypes = columnTypes;
  } else if (Array.isArray(rows) && rows.length) {
    mappedColumnTypes = inferColumnTypesFromRows(rows);
    if (mappedColumnTypes && Object.keys(mappedColumnTypes).length === 0) {
      mappedColumnTypes = undefined;
    }
  }

  const payload = {
    horizon: Number(horizon),
  };

  // Only include column_types if we actually have a non-empty mapping
  if (mappedColumnTypes && Object.keys(mappedColumnTypes).length > 0) {
    payload.column_types = mappedColumnTypes;
  }

  if (Array.isArray(rows) && rows.length) payload.rows = rows;          // demo / instant runs
  if (companyName) payload.company_name = companyName;
  if (typeof evaluation_mode === 'boolean') payload.evaluation_mode = evaluation_mode;
  if (userId) payload.user_id = userId;

  // ---- Call and surface real backend errors (instead of generic 400)
  let data;
  try {
    const res = await api.post(
      '/predictor/forecast',
      payload,
      { headers: { 'Content-Type': 'application/json', ...authHeader() } }
    );
    data = res.data;
  } catch (err) {
    const resp = err?.response;
    const detail =
      resp?.data?.detail ||
      resp?.data?.message ||
      (typeof resp?.data === 'string' ? resp.data : '') ||
      err?.message ||
      'Request failed';
    const e = new Error(`HTTP ${resp?.status || ''} ${detail}`.trim());
    e.status = resp?.status;
    e.body = resp?.data;
    throw e;
  }

  if (userId) {
    try {
      if (userCacheKey) setUserCache(userId, userCacheKey, { ts: Date.now(), payload: data });
      setUserCache(userId, 'forecast:last', { ts: Date.now(), payload: data });
    } catch {}
  }
  return data;
}

/* CFO Insights */
export async function fetchCfoInsights(arg = {}) {
  const token       = typeof arg === 'string' ? arg : arg?.token;
  const rows        = typeof arg === 'object' ? arg?.rows : undefined;
  const columnTypes = typeof arg === 'object' ? arg?.columnTypes || {} : {};
  const companyName = typeof arg === 'object' ? arg?.companyName || '' : '';
  const userId      = typeof arg === 'object' ? arg?.userId : undefined;

  const fp = Array.isArray(rows) && rows.length
    ? computeFingerprint({ rows, columnTypes, companyName })
    : null;

  let data;
  if (Array.isArray(rows) && rows.length) {
    // ✅ Use the stateless POST when we actually have the dataset in memory
    const res = await fetch(`${API_BASE}/cfo/insights`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ data: rows, columnTypes, companyName }),
    });
    data = await _handleFetch(res);
  } else {
    // Fallback: legacy GET reads app_state (may be empty → stub)
    const res = await fetch(`${API_BASE}/cfo/insights`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    data = await _handleFetch(res);
  }

  if (userId && fp) {
    try { setUserCache(userId, `cfo:${fp}`, data); } catch {}
  }
  return data;
}


/* Model Insights (fingerprinted per dataset) */
export function getCachedModelInsights(userId, fingerprint) {
  if (!userId) return null;
  const key = fingerprint ? `modelInsights:v1:${fingerprint}` : 'modelInsights:v1';
  try { return getUserCache(userId, key); } catch { return null; }
}

export function clearModelInsightsCache(userId, fingerprint) {
  try {
    if (!userId) return;
    if (fingerprint) setUserCache(userId, `modelInsights:v1:${fingerprint}`, null);
    setUserCache(userId, 'modelInsights:v1', null);
    if (window.__apiMem) {
      if (fingerprint) window.__apiMem.delete(`modelInsights:${userId}:v1:${fingerprint}`);
      window.__apiMem.delete(`modelInsights:${userId}:v1`);
    }
  } catch {}
}

export async function fetchModelInsights({ force = false, token, userId, fingerprint } = {}) {
  const ns = userId
    ? (fingerprint ? `modelInsights:${userId}:v1:${fingerprint}` : `modelInsights:${userId}:v1`)
    : 'modelInsights:v1';
  const mem = (window.__apiMem ||= new Map());
  const storageKey = (userId && fingerprint)
    ? `modelInsights:v1:${fingerprint}`
    : (userId ? 'modelInsights:v1' : null);

  const readCache = () => {
    if (mem.has(ns)) return mem.get(ns);
    try {
      const cached = (userId && storageKey) ? getUserCache(userId, storageKey) : null;
      if (!cached) return null;
      mem.set(ns, cached);
      return cached;
    } catch { return null; }
  };

  const writeCache = (val) => {
    mem.set(ns, val);
    try { if (userId && storageKey) setUserCache(userId, storageKey, val); } catch {}
  };

  if (!force) {
    const cached = readCache();
    const hasForecasts = Array.isArray(cached?.forecasts) && cached.forecasts.length > 0;
    const hasSeries = cached?.chart_series && Object.keys(cached.chart_series || {}).length > 0;
    if (hasForecasts || hasSeries) return cached;
  }

  const res = await fetch(`${API_BASE}/ai/analysis`, {
    method: 'GET',
    headers: {
      'Accept': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  const data = await _handleFetch(res);
  writeCache(data);
  return data;
}
/* (dedup) fetchDashboardPayload is defined once below with fingerprinted caching */

/* Strategy (fingerprinted per dataset) */
export function getCachedStrategy(userId, fingerprint) {
  if (!userId || !fingerprint) return null;
  try { return getUserCache(userId, `strategy:v1:${fingerprint}`) || null; } catch { return null; }
}

export function clearStrategyCache(userId, fingerprint) {
  try {
    if (!userId) return;
    if (fingerprint) setUserCache(userId, `strategy:v1:${fingerprint}`, null);
    setUserCache(userId, 'strategy:v1', null);
    if (window.__apiMem) {
      if (fingerprint) window.__apiMem.delete(`strategy:${userId}:v1:${fingerprint}`);
      window.__apiMem.delete(`strategy:${userId}:v1`);
    }
  } catch {}
}

export async function fetchStrategy(payload, { token, refresh = 0, wipe = 0 } = {}) {
  const qs = new URLSearchParams();
  if (refresh) qs.set('refresh', String(refresh));
  if (wipe)    qs.set('wipe', String(wipe));

  const res = await fetch(
    `/api/strategy/generate${qs.toString() ? `?${qs.toString()}` : ''}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(payload),
    }
  );
  if (!res.ok) throw new Error(`Strategy fetch failed: ${res.status}`);
  return await res.json();
}

export async function fetchDashboardPayloadLite() {
  const attempt = async () => {
    const res = await fetch(`${API_BASE}/dashboard/payload-lite`, {
      method: 'GET',
      headers: { 'Accept': 'application/json' },
      credentials: 'include'
    });
    if (!res.ok) throw new Error(String(res.status));
    return await res.json();
  };
  try {
    return await attempt();
  } catch (e) {
    const code = Number(String(e.message || '').replace(/\D/g, '')) || 0;
    if (code === 404 || code === 503) {
      await new Promise(r => setTimeout(r, 250)); // short backoff to avoid blink
      return await attempt();
    }
    throw new Error(`payload-lite failed: ${code || 'unknown'}`);
  }
}


/* ----------------------------
   Dashboard payload
   ---------------------------- */
export async function fetchDashboardPayload({ rows, columnTypes = {}, companyName = "" }) {
  const fp = computeFingerprint({ rows, columnTypes, companyName });
  const cacheKey = `dashboard:${fp}`;
  const cached = getCached("frontend", cacheKey);
  if (cached) return cached;

  if (!Array.isArray(rows) || rows.length === 0) {
    throw new Error("[Dashboard] rows must be a non-empty array");
  }

  const res = await fetch(`${API_BASE}/dashboard/payload`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ data: rows, columnTypes, companyName }),
  });

  const data = await _handleFetch(res);
  setCached("frontend", cacheKey, data);
  return data;
}

/* ----------------------------
   Classifier / AI Analysis / Chatbot
   ---------------------------- */
export async function classifyFile({ token }) {
  const res = await fetch(`${API_BASE}/classify/classify`, {
    method: 'POST',
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
  return _handleFetch(res);
}

export async function fetchAIAnalysis({ token }) {
  const res = await fetch(`${API_BASE}/ai/analysis`, {
    method: 'GET',
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
  return _handleFetch(res);
}

export async function askChatbot(arg) {
  const question = typeof arg === 'string' ? arg : arg?.question;
  const token    = typeof arg === 'object' ? arg?.token   : undefined;
  const userId   = typeof arg === 'object' ? arg?.userId  : undefined;

  if (!question || typeof question !== 'string' || !question.trim()) {
    throw new Error('Chatbot request missing "question"');
  }

  const res = await fetch(`${API_BASE}/chatbot/ask`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ question, user_id: userId || null }),
  });
  return _handleFetch(res);
}

/* Chatbot history (server-side) */
export async function fetchChatHistory(userId, token) {
  if (!userId) return [];
  const res = await fetch(`${API_BASE}/chatbot/history?user_id=${encodeURIComponent(userId)}`, {
    method: 'GET',
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
  const data = await _handleFetch(res);
  return Array.isArray(data?.messages) ? data.messages : [];
}

export async function saveChatHistoryServer(userId, messages, mode = 'append', token) {
  if (!userId) return;
  const payload = (Array.isArray(messages) ? messages : []).map(m => ({
    role: m.role,
    content: m.content,
    ts: m.ts || Math.floor(Date.now() / 1000),
  }));
  const res = await fetch(`${API_BASE}/chatbot/history`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ user_id: userId, messages: payload, mode }),
  });
  return _handleFetch(res);
}

export async function clearChatHistory(userId, token) {
  if (!userId) return;
  const res = await fetch(`${API_BASE}/chatbot/history?user_id=${encodeURIComponent(userId)}`, {
    method: 'DELETE',
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
  return _handleFetch(res);
}

// ---- Billing helpers ----
export async function openBillingPortal(token) {
  const res = await fetch(`${API_BASE}/billing/portal`, {
    method: 'POST',
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
  return _handleFetch(res);
}

export async function cancelSubscription({ atPeriodEnd = true, token } = {}) {
  const url = `${API_BASE}/billing/cancel?at_period_end=${atPeriodEnd ? 'true' : 'false'}`;
  const res = await fetch(url, {
    method: 'POST',
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
  return _handleFetch(res);
}

export async function adminCancelSubscription(email, { atPeriodEnd = true, token } = {}) {
  const url = `${API_BASE}/billing/admin/cancel?email=${encodeURIComponent(email)}&at_period_end=${atPeriodEnd ? 'true' : 'false'}`;
  const res = await fetch(url, {
    method: 'POST',
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
  return _handleFetch(res);
}
// ---- Analytics (admin) ----
export async function postTrack(body = {}, token) {
  const res = await fetch(`${API_BASE}/analytics/track`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(body),
  });
  return _handleFetch(res);
}


export async function getAnalyticsSummary({ from, from_, to } = {}, token) {
  const qp = new URLSearchParams();
  const start = from_ || from;   // accept both, send the one backend expects
  if (start) qp.set('from_', start);
  if (to) qp.set('to', to);
  const res = await fetch(`${API_BASE}/admin/analytics/summary?${qp.toString()}`, {
    headers: (token ? { Authorization: `Bearer ${token}` } : {}),
  });
  return _handleFetch(res);
}


export async function getAnalyticsTimeseries(metric = 'signups', days = 90, token) {
  const qp = new URLSearchParams({ metric, days: String(days) });
  const res = await fetch(`${API_BASE}/admin/analytics/timeseries?${qp.toString()}`, {
    headers: (token ? { Authorization: `Bearer ${token}` } : {}),
  });
  return _handleFetch(res);
}


export async function getAnalyticsSources(days = 30, token) {
  const qp = new URLSearchParams({ days: String(days) });
  const res = await fetch(`${API_BASE}/admin/analytics/sources?${qp.toString()}`, {
    headers: (token ? { Authorization: `Bearer ${token}` } : {}),
  });
  return _handleFetch(res);
}

export async function fetchTransactionReport(batchId, { months = 6, token } = {}) {
  const res = await fetch(
    `${API_BASE}/transactions/report?batch_id=${encodeURIComponent(batchId)}&months=${months}`,
    { headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) } }
  );
  return _handleFetch(res);
}

/* =========================
   Transactions API helpers
   ========================= */
export async function uploadTransactions(file, { token, userId } = {}) {
  const fd = new FormData();
  fd.append('file', file);
  if (userId) fd.append('user_id', userId);

  const res = await fetch(`${API_BASE}/transactions/upload`, {
    method: 'POST',
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: fd,
  });

  let body = {};
  try {
    body = await res.json();
  } catch (_) {
    // ignore JSON parse errors
  }

  if (!res.ok) {
    const msg = body?.detail || `Upload failed (${res.status})`;
    throw new Error(msg);
  }

  return body;
}


export async function classifyTransactions(batchId, { use_gpt = true, token } = {}) {
  const res = await fetch(
    `${API_BASE}/transactions/classify?batch_id=${encodeURIComponent(batchId)}&use_gpt=${use_gpt ? 'true' : 'false'}`,
    { method: 'POST', headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) } }
  );
  return (await res.json());
}

export async function reconcileTransactions(batchId, { token } = {}) {
  const res = await fetch(
    `${API_BASE}/transactions/reconcile?batch_id=${encodeURIComponent(batchId)}`,
    { method: 'POST', headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) } }
  );
  return (await res.json());
}

export async function fetchTransactionTimeseries(batchId, { token } = {}) {
  const res = await fetch(
    `${API_BASE}/transactions/timeseries?batch_id=${encodeURIComponent(batchId)}`,
    { headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) } }
  );
  return (await res.json());
}

export async function finalizeTransactionsToDashboard(batchId, { token } = {}) {
  const res = await fetch(
    `${API_BASE}/transactions/finalize?batch_id=${encodeURIComponent(batchId)}`,
    { method: 'POST', headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) } }
  );
  return (await res.json());
}
